import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { webauthnSupported, webauthnGet } from "@/lib/webauthnHelpers";
import { Fingerprint, CheckCircle2, LogOut, AlertTriangle, Clock } from "lucide-react";

/**
 * Clock kiosk — runs on a tablet/phone/PC at the door. Staff place their
 * enrolled finger (Windows Hello / Android fingerprint / FIDO2 USB reader);
 * the assertion identifies them and records check-in or check-out.
 */
export default function AttendanceKiosk({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null); // {action, staff, record}
  const [error, setError] = useState(null);

  const clock = async () => {
    setError(null);
    if (!webauthnSupported()) {
      setError("This browser does not support fingerprint (WebAuthn). Use Chrome/Edge on a device with a fingerprint reader, over HTTPS.");
      return;
    }
    setBusy(true);
    try {
      const { data: options } = await api.post("/attendance/webauthn/clock-options");
      const assertion = await webauthnGet(options);
      const { data } = await api.post("/attendance/webauthn/clock-verify", assertion);
      setLast(data);
      onChanged?.();
    } catch (e) {
      if (e?.name === "NotAllowedError") setError("Cancelled or timed out — try again.");
      else setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="attendance-kiosk">
      <div className="bg-[#022C22] text-white rounded-2xl p-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-200/80">Staff attendance kiosk</div>
        <div className="font-display text-2xl font-bold mt-1">Place your finger to register</div>
        <button onClick={clock} disabled={busy} data-testid="kiosk-clock-button"
          className="mt-8 mx-auto h-36 w-36 rounded-full bg-amber-500 hover:bg-amber-400 active:scale-95 transition-all flex items-center justify-center disabled:opacity-60">
          <Fingerprint size={72} className="text-emerald-950" strokeWidth={1.4} />
        </button>
        <div className="text-xs text-emerald-200/70 mt-6">
          {busy ? "Waiting for fingerprint…" : "Tap the button, then place your enrolled finger on the reader"}
        </div>
      </div>

      {last && (
        <div className={`mt-4 rounded-xl border p-4 flex items-start gap-3 ${
          last.action === "check_in" ? "border-emerald-200 bg-emerald-50"
            : last.action === "duplicate" ? "border-gray-200 bg-gray-50" : "border-amber-200 bg-amber-50"
        }`} data-testid="kiosk-result">
          {last.action === "check_in"
            ? <CheckCircle2 size={22} className="text-emerald-700 shrink-0 mt-0.5" />
            : last.action === "duplicate"
              ? <Clock size={22} className="text-gray-500 shrink-0 mt-0.5" />
              : <LogOut size={22} className="text-amber-700 shrink-0 mt-0.5" />}
          <div>
            <div className="font-semibold text-gray-900" data-testid="kiosk-result-name">
              {last.staff?.name} <span className="text-gray-400 font-normal">· {last.staff?.staff_number}</span>
            </div>
            <div className="text-sm text-gray-600 mt-0.5">
              {last.action === "check_in" && <>Checked <strong>IN</strong> at {last.record?.check_in}
                {last.record?.status === "late" && <span className="text-red-600 font-semibold"> (late)</span>}</>}
              {last.action === "check_out" && <>Checked <strong>OUT</strong> at {last.record?.check_out}</>}
              {last.action === "duplicate" && "Already registered a moment ago"}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3 text-sm text-red-800"
          data-testid="kiosk-error">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <div className="mt-4 text-[11px] text-gray-400 text-center">
        Works with built-in fingerprint sensors (Windows Hello, Android, Touch ID) and FIDO2 USB
        fingerprint readers. Door terminals report through the Devices tab instead.
      </div>
    </div>
  );
}
